import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useContentType } from '@confluence/page-context';

import type {
	useActiveInlineDataActiveInlineDataQuery$data,
	useActiveInlineDataActiveInlineDataQuery$variables,
} from './__generated__/useActiveInlineDataActiveInlineDataQuery.graphql.ts';

const useActiveInlineDataActiveInlineDataQuery = graphql`
	query useActiveInlineDataActiveInlineDataQuery($cloudId: ID!, $contentId: ID!) {
		confluence_activeInlineData(cloudId: $cloudId, contentId: $contentId) {
			reactions {
				commentId
				markerRef
				type
			}
		}
	}
`;

export type UseActiveInlineDataResult = {
	reactionMarkerRefs: string[];
	reactionMarkerMap: Record<string, string>;
	loading: boolean;
	error: Error | undefined;
};

export const useActiveInlineData = (contentId: string): UseActiveInlineDataResult => {
	const { cloudId } = useSessionData();
	const [contentType] = useContentType();
	const { data, loading, error } = useQueryInterop<
		useActiveInlineDataActiveInlineDataQuery$data,
		useActiveInlineDataActiveInlineDataQuery$variables
	>(useActiveInlineDataActiveInlineDataQuery, {
		variables: {
			cloudId,
			contentId: `ari:cloud:confluence:${cloudId}:${contentType === 'blogpost' ? 'blogpost' : 'page'}/${contentId}`,
		},
		skip: !expVal('confluence_inline_reactions', 'isEnabled', false),
	});

	return useMemo(() => {
		const reactions = data?.confluence_activeInlineData?.reactions ?? [];
		const reactionMarkerRefs: string[] = [];
		const reactionMarkerMap = reactions.reduce<Record<string, string>>((acc, marker) => {
			if (marker) {
				reactionMarkerRefs.push(marker.markerRef);
				acc[marker.markerRef] = marker.commentId;
			}
			return acc;
		}, {});

		return {
			reactionMarkerRefs,
			reactionMarkerMap,
			loading,
			error,
		};
	}, [data, loading, error]);
};
